/**
 * useBehaviorAnalysis - 摄像头行为分析 Hook
 *
 * 与摄像头开关联动：摄像头开则分析，关则停止
 *
 * 使用方式：
 *   const { currentAnalysis, summary, isModelLoaded } = useBehaviorAnalysis(
 *     videoRef,
 *     !isCameraOff,
 *   );
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import {
  initializeModels,
  getModels,
  disposeModels,
  FrameAnalyzer,
  generateSummary,
} from '../services/behaviorAnalysisService';
import type { FrameAnalysis, BehaviorSummary } from '../types';

export interface UseBehaviorAnalysisReturn {
  /** 最新的单帧分析结果 */
  currentAnalysis: FrameAnalysis | null;
  /** 面试全程行为分析摘要 */
  summary: BehaviorSummary | null;
  /** 模型是否已加载就绪 */
  isModelLoaded: boolean;
  /** 模型是否正在加载中 */
  isLoading: boolean;
  /** 是否发生错误 */
  error: string | null;
  /** 重置摘要 */
  resetSummary: () => void;
  /** 获取当前时间线的摘要快照（可在组件卸载前直接调用） */
  getTimelineSnapshot: () => BehaviorSummary | null;
  /** 手动停止分析（停止 rAF + 生成最终摘要），不关闭摄像头 */
  stopAnalysis: () => void;
}

export function useBehaviorAnalysis(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  enabled: boolean,
): UseBehaviorAnalysisReturn {
  const [currentAnalysis, setCurrentAnalysis] =
    useState<FrameAnalysis | null>(null);
  const [summary, setSummary] = useState<BehaviorSummary | null>(null);
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ref 避免闭包陷阱
  const enabledRef = useRef(enabled);
  const rafRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);
  const timelineRef = useRef<FrameAnalysis[]>([]);
  const analyzerRef = useRef<FrameAnalyzer | null>(null);
  const summaryGeneratedRef = useRef(false);
  const stoppedRef = useRef(false);

  enabledRef.current = enabled;

  // ── 核心分析循环 ──
  const analyzeFrame = useCallback(() => {
    if (!enabledRef.current) return;

    const video = videoRef.current;
    if (!video || video.readyState < 2) {
      rafRef.current = requestAnimationFrame(analyzeFrame);
      return;
    }

    const models = getModels();
    if (!models.faceLandmarker || !models.poseLandmarker || !analyzerRef.current) {
      rafRef.current = requestAnimationFrame(analyzeFrame);
      return;
    }

    const timestamp = performance.now();

    // MediaPipe 检测
    let faceResult = null;
    let poseResult = null;

    try {
      faceResult = models.faceLandmarker.detectForVideo(video, timestamp);
    } catch {
      // 单帧检测失败不中断
    }

    try {
      poseResult = models.poseLandmarker.detectForVideo(video, timestamp);
    } catch {
      // 单帧检测失败不中断
    }

    const result = analyzerRef.current.analyze(video, faceResult, poseResult);
    if (result) {
      timelineRef.current.push(result);
      setCurrentAnalysis(result);
    }

    rafRef.current = requestAnimationFrame(analyzeFrame);
  }, [videoRef]);

  // ── 启用分析（enabled 从 false → true） ──
  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    const boot = async () => {
      setIsLoading(true);
      setError(null);

      try {
        await initializeModels();

        if (cancelled) return;

        const models = getModels();
        if (!models.faceLandmarker || !models.poseLandmarker) {
          throw new Error('模型加载后仍不可用');
        }

        setIsModelLoaded(true);
        setIsLoading(false);

        // 新分析器
        analyzerRef.current = new FrameAnalyzer();
        startTimeRef.current = Date.now();
        summaryGeneratedRef.current = false;
        stoppedRef.current = false;

        // 启动循环
        rafRef.current = requestAnimationFrame(analyzeFrame);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : '行为分析模型加载失败',
          );
          setIsModelLoaded(false);
          setIsLoading(false);
        }
      }
    };

    boot();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // ── 禁用分析（enabled 从 true → false） ──
  useEffect(() => {
    if (enabled) return;

    // 如果已被手动 stopAnalysis() 处理过，跳过
    if (stoppedRef.current) return;

    // 停止动画循环
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }

    // 生成摘要
    if (timelineRef.current.length > 0 && !summaryGeneratedRef.current) {
      summaryGeneratedRef.current = true;
      const duration = Math.round((Date.now() - startTimeRef.current) / 1000);
      const generatedSummary = generateSummary(
        timelineRef.current,
        Math.max(duration, 1),
      );
      setSummary(generatedSummary);
    }

    // 释放分析器
    analyzerRef.current = null;

    // 注意：不释放 MediaPipe 模型，因为用户可能很快再次打开摄像头
    // 模型释放交给组件卸载时处理
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // ── 组件卸载时释放所有资源 ──
  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
      analyzerRef.current = null;
      disposeModels();
    };
  }, []);

  // ── 手动停止分析（不关摄像头，仅停 rAF + 生成摘要） ──
  const stopAnalysis = useCallback(() => {
    if (stoppedRef.current) return;
    stoppedRef.current = true;

    // 停止 rAF
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }

    // 生成最终摘要
    if (timelineRef.current.length > 0 && !summaryGeneratedRef.current) {
      summaryGeneratedRef.current = true;
      const duration = Math.round((Date.now() - startTimeRef.current) / 1000);
      const generatedSummary = generateSummary(
        timelineRef.current,
        Math.max(duration, 1),
      );
      setSummary(generatedSummary);
    }

    // 释放分析器
    analyzerRef.current = null;
  }, []);

  // ── 重置 ──
  const resetSummary = useCallback(() => {
    timelineRef.current = [];
    setSummary(null);
    setCurrentAnalysis(null);
    summaryGeneratedRef.current = false;
  }, []);

  // ── 获取当前时间线快照 ──
  const getTimelineSnapshot = useCallback(() => {
    if (timelineRef.current.length === 0) return null;
    const duration = Math.round((Date.now() - startTimeRef.current) / 1000);
    return generateSummary(timelineRef.current, Math.max(duration, 1));
  }, []);

  return {
    currentAnalysis,
    summary,
    isModelLoaded,
    isLoading,
    error,
    resetSummary,
    getTimelineSnapshot,
    stopAnalysis,
  };
}
